import { DRM_ACCOUNT_TYPES, URL_PATTERN_FOR_TEMPLATES } from '../../../../app/core/constants'
import {
  transcodingStatusDisplayText,
  transcodingStatusStateCssClass
} from '../../../../app/helpers/streamWorkState'
import {
  highlightClassSimple,
  TypeaheadClass
} from '../../../../app/helpers/typeahead'
import { changeStreamUrl, filterDRM, getFilteredKeys, multiSelectWithAll } from './ciHelpers'
import { streamPolicyOperators, streamPolicyPropertiesNames } from '../../../streams/urlRule.directive'
import { evaluateSkipSegments } from '../../../../app/helpers/skipIntroCredits'
import { hasSelectedDrmProfile } from '../../../../app/helpers/offlinePlayback'

/**
 * Directive for Stream Policy.
 *
 * @class ciStreamPolicy
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciStreamPolicy
 *
 */
/* @ngInject */
export function ciStreamPolicy (UrlPolicy, TemplateVariable, $uibModal, $http, alertLiteSvc, Stream, SetrixServer, InappSettings, authSvc) {
  function getCondition (scope) {
    return (conditionRule) => UrlPolicy.getCondition(conditionRule, scope.entity)
  }

  function canModifyAssignmentTranscoder () {
    return !!authSvc?.hasAccess?.('modifyAssignmentTranscoder')
  }

  function toStreamBinding (modalResult) {
    if (!modalResult) return null

    const streamId = modalResult.transcoderId ?? null
    if (!streamId || !modalResult.setrixServerId) return null

    return {
      streamId,
      streamName: modalResult.transcoderName ?? modalResult.templateName ?? null,
      server: {
        id: modalResult.setrixServerId,
        name: modalResult.setrixServerName ?? null,
        networkId: modalResult.setrixServerNetworkId ?? null
      }
    }
  }

  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciStreamPolicy.html',
    scope: {
      form: '=form',
      entity: '=entity',
      drmProfiles: '=drmProfiles',
      playbackTokenGenerators: '=playbackTokenGenerators'

    },
    link: function (scope) {
      scope.transcodingStatusStateCssClass = transcodingStatusStateCssClass
      scope.transcodingStatusDisplayText = transcodingStatusDisplayText
      scope.hasAccess = function (access) {
        return !!authSvc?.hasAccess?.(access, scope)
      }
      scope.urlPolicies = []
      scope.loadPolicies = UrlPolicy.getPolicies(scope.urlPolicies)
      scope.getCondition = getCondition(scope)
      scope.loadPolicies()

      let setrixPrefetchKey = null
      function unwrapSetrixPayload (data) {
        if (!data) return data
        if (typeof data === 'object' && data.status && data.payload) return data.payload
        if (typeof data === 'object' && data.payload) return data.payload
        return data
      }
      function pickSetrixServerList (payload) {
        if (!payload) return []
        if (Array.isArray(payload)) return payload
        return (
          payload.streams ||
          payload.items ||
          payload.data ||
          payload.results ||
          payload.content ||
          []
        )
      }
      function getSetrixServerListFromResponse (response) {
        return pickSetrixServerList(unwrapSetrixPayload(response?.data))
      }
      function assignedNetworkIdSignature () {
        const list = Array.isArray(scope.entity?.networks) ? scope.entity.networks : []
        const ids = list
          .map((n) => n?.id)
          .filter((id) => id != null && String(id).trim() !== '')
          .map((id) => String(id))
          .sort()
        return ids.join(',')
      }
      function buildAllowedNetworkIdSet () {
        const sig = assignedNetworkIdSignature()
        if (!sig) return null
        return new Set(sig.split(',').filter(Boolean))
      }
      function filterServersByAssignedNetworks (servers, multiorgEnabled) {
        const list = Array.isArray(servers) ? servers : []
        if (!multiorgEnabled) return list
        const allowed = buildAllowedNetworkIdSet()
        if (!allowed || allowed.size === 0) return list
        return list.filter((s) => {
          const id = s?.networkId
          if (id == null) return false
          return allowed.has(String(id))
        })
      }
      function refreshSetrixServersPrefetch () {
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        const key = `${multiorgEnabled ? 1 : 0}:${
          multiorgEnabled ? assignedNetworkIdSignature() : (scope.entity?.network?.id ?? '')
        }`
        if (key === setrixPrefetchKey && scope.setrixServersPrefetchPromise) return
        setrixPrefetchKey = key
        scope.hasAvailableSetrixServers = false
        // In multi-org mode we intentionally fetch all servers and filter client-side
        // by content-assigned organizations (entity.networks[].id vs server.networkId).
        const requestNetworkId = multiorgEnabled ? null : (scope.entity?.network?.id ?? null)
        scope.setrixServersPrefetchPromise = SetrixServer.loadAll(requestNetworkId, multiorgEnabled)
        scope.setrixServersPrefetchPromise
          .then((response) => {
            const list = getSetrixServerListFromResponse(response)
            const filtered = filterServersByAssignedNetworks(list, multiorgEnabled)
            scope.setrixServersPrefetchList = filtered
            scope.hasAvailableSetrixServers = Array.isArray(filtered) && filtered.length > 0

            const byId = new Map(
              (Array.isArray(list) ? list : [])
                .filter((s) => s && (s.id != null || s.serverId != null))
                .map((s) => [String(s.id ?? s.serverId), s.name ?? s.serverName ?? null])
            )

            const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
            bindings.forEach((binding) => {
              const serverId = binding?.server?.id
              if (serverId == null) return
              const serverName = byId.get(String(serverId))
              if (!serverName) return
              if (!binding.server) binding.server = { id: serverId }
              binding.server.name = binding.server.name ?? serverName
            })
          })
          .catch(() => {
            scope.setrixServersPrefetchList = []
            scope.hasAvailableSetrixServers = false
          })
      }
      refreshSetrixServersPrefetch()
      scope.$watchGroup([
        () => !!InappSettings?._current?.multiorgEnabled,
        assignedNetworkIdSignature,
        () => scope.entity?.network?.id
      ], refreshSetrixServersPrefetch)

      scope.isStreamPolicyOpen = scope.entity._isNew()
      scope.urlPattern = URL_PATTERN_FOR_TEMPLATES

      function ensureDefaultStreamMediaUrl () {
        if (!scope.entity?.defaultStream) return
        if (!scope.entity.defaultStream.mediaUrl) {
          scope.entity.defaultStream.mediaUrl = { drmProfiles: [] }
        }
      }
      ensureDefaultStreamMediaUrl()
      scope.$watch('entity.defaultStream', ensureDefaultStreamMediaUrl, true)

      TemplateVariable.loadAllVariables()
        .to(scope.templateVariables = [])
        .then(() => {
          scope.setDefaultStreamUrlValue = function (val) {
            scope.entity.defaultStream.mediaUrl.mediaUrl = val
          }
          scope.typeaheadUrl = new TypeaheadClass(scope.templateVariables, scope.setDefaultStreamUrlValue, changeStreamUrl)

          scope.setDefaultTrailerUrlValue = function (val) {
            scope.entity.defaultStream.mediaUrl.trailerUrl = val
          }
          scope.typeaheadDefaultTrailerUrl = new TypeaheadClass(scope.templateVariables, scope.setDefaultTrailerUrlValue, changeStreamUrl)
        })

      scope.highlightClassSimple = (model) => highlightClassSimple(scope.templateVariables, model)

      scope.countUrls = function (arr = scope.entity.streamPolicy.$rules) {
        return arr?.reduce((acc, rule) => acc + !!rule?.then?.mediaUrl?.mediaUrl + scope.countUrls(rule.$subRules), 0)
      }

      scope.getFilteredKeys = getFilteredKeys
      scope.multiSelectWithAll = multiSelectWithAll
      let streamStatusesPollingId = null
      let streamStatusesInitialized = false

      scope.requestAssignedStreamsStatus = function () {
        const bindings = Array.isArray(scope.entity.streamBindings) ? scope.entity.streamBindings : []
        const streamIds = bindings.map((binding) => binding?.streamId).filter(Boolean)
        if (!streamIds.length) return Promise.resolve()
        const assignedTranscoders = Stream.buildAssignedTranscodersFromBindings(bindings)

        return Stream.loadStatuses(streamIds, assignedTranscoders)
          .then((items) => {
            const statusItems = Array.isArray(items) ? items : []
            const statusesByExternalId = new Map(
              statusItems
                .filter(item => item?.externalId)
                .map(item => [item.externalId, item])
            )

            bindings.forEach((binding) => {
              const matched = statusesByExternalId.get(binding?.streamId)
              if (!matched) return

              binding.streamName = matched?.name ?? binding.streamName
              binding.status = matched?.status?.statusState ?? binding.status
              binding.statusLabel = matched?.status?.statusLabel ?? binding.statusLabel
              binding.active = matched?.status?.active ?? binding.active
              const activeFlag = matched?.status?.active
              if (activeFlag !== undefined && activeFlag !== null && !binding._streamToggleInProgress) {
                binding.enabled = !!activeFlag
              }
            })
          })
      }

      function startStreamStatusesPolling () {
        if (streamStatusesPollingId) return
        streamStatusesPollingId = setInterval(() => {
          scope.requestAssignedStreamsStatus()
        }, 10000)
      }

      function hasAssignedStreams () {
        return Array.isArray(scope.entity?.streamBindings) && scope.entity.streamBindings.some((binding) => !!binding?.streamId)
      }

      function initStreamStatusesIfReady () {
        if (streamStatusesInitialized) return
        if (!scope.entity?.id) return
        streamStatusesInitialized = true

        if (hasAssignedStreams()) {
          scope.requestAssignedStreamsStatus()
            .finally(() => {
              startStreamStatusesPolling()
            })
        }
      }

      function autofillEmptyPolicyRuleStreamUrls (url) {
        if (!url || ['show'].includes(scope.entity?.$itemType)) return
        const rules = scope.entity?.streamPolicy?.$rules
        if (!Array.isArray(rules)) return

        const fillThen = (thenObj) => {
          if (!thenObj?.mediaUrl || thenObj.mediaUrl.mediaUrl) return
          thenObj.mediaUrl.mediaUrl = url
          changeStreamUrl({ obj: thenObj })
        }

        for (const rule of rules) {
          if (rule.$subRules?.length) {
            for (const subRule of rule.$subRules) {
              fillThen(subRule.then)
            }
            fillThen(rule.then)
          } else {
            fillThen(rule.then)
          }
        }
      }

      function shouldAutofillFromTranscoderOutputs (uris) {
        return Array.isArray(uris) && uris.length === 1
      }

      function autofillEmptyStreamUrlsFromTranscoderOutputs (uris) {
        if (!shouldAutofillFromTranscoderOutputs(uris)) return
        const url = uris[0]
        if (scope.entity?.defaultStream?.mediaUrl && !scope.entity.defaultStream.mediaUrl.mediaUrl) {
          scope.entity.defaultStream.mediaUrl.mediaUrl = url
          changeStreamUrl({ obj: scope.entity.defaultStream })
        }
        autofillEmptyPolicyRuleStreamUrls(url)
      }

      scope.changePolicy = function () {
        scope.entity.$toPolicedStreams = { }
        if (shouldAutofillFromTranscoderOutputs(scope.transcoderOutputUrls)) {
          autofillEmptyStreamUrlsFromTranscoderOutputs(scope.transcoderOutputUrls)
        }
      }
      scope.addTranscoder = function () {
        if (!canModifyAssignmentTranscoder()) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        const modalInstance = $uibModal.open({
          animation: false,
          templateUrl: 'components/common/directives/contentItems/views/addTranscoderModal.html',
          controller: 'AddTranscoderModalCtrl',
          size: 'lg',
          resolve: {
            networkId: () => null,
            setrixServersPrefetch: () => (
              scope.setrixServersPrefetchList ||
              scope.setrixServersPrefetchPromise ||
              SetrixServer.loadAll(null, !!InappSettings?._current?.multiorgEnabled)
            ),
            assignedStreamBindings: () => {
              const list = scope.entity?.streamBindings
              if (!Array.isArray(list)) return []
              return list.map((b) => ({
                streamId: b?.streamId,
                serverId: b?.server?.id ?? null
              }))
            }
          }
        })

        modalInstance.result.then((it) => {
          const binding = toStreamBinding(it)
          if (!binding) return

          if (!Array.isArray(scope.entity.streamBindings)) {
            scope.entity.streamBindings = []
          }
          scope.entity.streamBindings.push(binding)
          scope.requestAssignedStreamsStatus()
          startStreamStatusesPolling()

          if (scope.form) {
            scope.form.$setDirty()
          }
        }).catch(() => {})
      }

      scope.deleteTranscoder = function (binding, index) {
        if (!canModifyAssignmentTranscoder()) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        if (!Array.isArray(scope.entity.streamBindings)) return
        const idx = Number.isInteger(index) ? index : scope.entity.streamBindings.indexOf(binding)
        if (idx < 0) return

        scope.entity.streamBindings.splice(idx, 1)

        if (scope.form) {
          scope.form.$setDirty()
        }
      }

      scope.onStreamEnabledChange = function (binding) {
        if (['show'].includes(scope.entity.$itemType)) return
        if (binding._streamToggleInProgress) return
        if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
          alertLiteSvc.alertError('Permission denied')
          binding.enabled = !binding.enabled
          return
        }

        const serverId = binding?.server?.id
        const streamId = binding?.streamId
        const wantEnabled = !!binding.enabled

        if (!serverId || streamId == null) {
          alertLiteSvc.alertError('Setrix server or stream is missing')
          binding.enabled = !wantEnabled
          return
        }

        binding._streamToggleInProgress = true
        const networkId = binding?.server?.networkId ?? scope.entity?.network?.id ?? null
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        const request = wantEnabled
          ? SetrixServer.startStream(serverId, streamId, networkId, multiorgEnabled)
          : SetrixServer.stopStream(serverId, streamId, networkId, multiorgEnabled)

        request
          .then(() => {
            alertLiteSvc.alert(`Transcoder ${wantEnabled ? 'started' : 'stopped'} successfully`, 'success')
            scope.requestAssignedStreamsStatus()
            if (scope.form) {
              scope.form.$setDirty()
            }
          })
          .catch(() => {
            binding.enabled = !wantEnabled
            alertLiteSvc.alertError(`Failed to ${wantEnabled ? 'started' : 'stopped'} transcoder`)
          })
          .finally(() => {
            binding._streamToggleInProgress = false
          })
      }

      scope.refreshTranscoder = function (binding) {
        if (binding?._restartInProgress) return
        if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        const serverId = binding?.server?.id
        const streamId = binding?.streamId
        if (!serverId || streamId == null) {
          alertLiteSvc.alertError('Setrix server or stream is missing')
          return
        }
        binding._restartInProgress = true
        const networkId = binding?.server?.networkId ?? scope.entity?.network?.id ?? null
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        SetrixServer.restartStream(serverId, streamId, networkId, multiorgEnabled)
          .then(() => {
            alertLiteSvc.alert('Transcoder restarted successfully', 'success')
            scope.requestAssignedStreamsStatus()
          })
          .catch((rejection) => {
            alertLiteSvc.alertError(rejection?.statusText || 'Failed to restart stream')
          })
          .finally(() => {
            binding._restartInProgress = false
          })
      }

      let defaultStreamOutputsRequestSeq = 0

      function streamBindingsOutputsSignature () {
        const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
        return bindings
          .map((b) => `${b?.server?.id ?? ''}:${b?.streamId ?? ''}`)
          .sort()
          .join('|')
      }

      scope.hasAssignedTranscoders = function () {
        const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
        return bindings.some((binding) => binding?.streamId)
      }

      scope.showSelectFromTranscoder = function () {
        return !!scope.hasAvailableSetrixServers && scope.hasAssignedTranscoders()
      }

      scope.transcoderOutputUrls = []
      scope.transcoderOutputUrlsLoading = false

      function refreshTranscoderOutputUrls () {
        const seq = ++defaultStreamOutputsRequestSeq
        const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
        const streamIds = bindings.map((binding) => binding?.streamId).filter(Boolean)

        if (!streamIds.length) {
          if (seq === defaultStreamOutputsRequestSeq) {
            scope.transcoderOutputUrls = []
            scope.defaultStreamUrlSuggestions = []
            scope.transcoderOutputUrlsLoading = false
          }
          return Promise.resolve()
        }

        scope.transcoderOutputUrlsLoading = true
        const assignedTranscoders = Stream.buildAssignedTranscodersFromBindings(bindings)
        return Stream.loadOutputs(streamIds, assignedTranscoders)
          .then((data) => {
            if (seq !== defaultStreamOutputsRequestSeq) return
            const uris = Stream.collectOutputUris(data || [])
            scope.transcoderOutputUrls = uris
            scope.defaultStreamUrlSuggestions = uris
            autofillEmptyStreamUrlsFromTranscoderOutputs(uris)
          })
          .catch(() => {
            if (seq !== defaultStreamOutputsRequestSeq) return
            scope.transcoderOutputUrls = []
            scope.defaultStreamUrlSuggestions = []
          })
          .finally(() => {
            if (seq !== defaultStreamOutputsRequestSeq) return
            scope.transcoderOutputUrlsLoading = false
          })
      }

      scope.refreshTranscoderOutputUrls = refreshTranscoderOutputUrls

      scope.applyTranscoderUrl = function (url, target) {
        if (!url || !target || target.type !== 'default') return

        if (!scope.entity?.defaultStream) return
        if (!scope.entity.defaultStream.mediaUrl) {
          scope.entity.defaultStream.mediaUrl = { drmProfiles: [] }
        }
        scope.entity.defaultStream.mediaUrl.mediaUrl = url
        changeStreamUrl({ obj: scope.entity.defaultStream })

        const fieldName = target.formField
        if (fieldName && scope.form?.[fieldName]) {
          scope.form[fieldName].$setTouched()
        }
        if (typeof scope.form?.$setDirty === 'function') {
          scope.form.$setDirty()
        }
      }

      scope.applyTranscoderUrlToDefault = function (url) {
        scope.applyTranscoderUrl(url, {
          type: 'default',
          formField: 'defaultStreamUrl'
        })
      }

      scope.applyTranscoderUrlToRule = function (url, rule) {
        if (!url || !rule?.then) return

        if (!rule.then.mediaUrl) {
          rule.then.mediaUrl = { drmProfiles: [] }
        }
        rule.then.mediaUrl.mediaUrl = url
        changeStreamUrl({ obj: rule.then })

        const fieldName = rule.id != null ? `streamUrl_${rule.id}` : null
        if (fieldName && scope.form?.[fieldName]) {
          scope.form[fieldName].$setTouched()
        }
        if (typeof scope.form?.$setDirty === 'function') {
          scope.form.$setDirty()
        }
      }

      scope.getDefaultUrlMatches = function (viewValue) {
        const varMatches = scope.typeaheadUrl?.getMatches?.(viewValue)
        return Array.isArray(varMatches) ? varMatches : []
      }

      scope.onDefaultStreamUrlSelect = function (item) {
        if (item == null) return

        const ctx = scope.typeaheadUrl?.cursorContext
        const canUseTemplateInsertion = ctx && !ctx.invalid && typeof ctx.text === 'string'

        if (canUseTemplateInsertion) {
          scope.typeaheadUrl.onSelect(item, null, { obj: scope.entity.defaultStream })
          return
        }

        if (typeof scope.setDefaultStreamUrlValue === 'function') {
          scope.setDefaultStreamUrlValue(item)
        } else {
          scope.entity.defaultStream.mediaUrl.mediaUrl = item
        }
        changeStreamUrl({ obj: scope.entity.defaultStream })
      }
      scope.changeStreamUrl = changeStreamUrl
      scope.changeDrmProfile = (platformName) => {
        if (platformName.drmProfiles.length) {
          platformName.kek = null
        }
      }
      scope.checkTrailerRequired = function () {
        return false
      }
      initStreamStatusesIfReady()
      const unwatchEntityId = scope.$watch('entity.id', function () {
        initStreamStatusesIfReady()
      })
      const unwatchStreamBindingsOutputs = scope.$watch(streamBindingsOutputsSignature, refreshTranscoderOutputUrls)

      scope.$on('$destroy', function () {
        if (typeof unwatchEntityId === 'function') {
          unwatchEntityId()
        }
        if (typeof unwatchStreamBindingsOutputs === 'function') {
          unwatchStreamBindingsOutputs()
        }
        if (streamStatusesPollingId) {
          clearInterval(streamStatusesPollingId)
          streamStatusesPollingId = null
        }
      })
    }
  }
}

/**
 * Directive for Stream Policy Rule.
 *
 * @class ciStreamPolicyRule
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciStreamPolicyRule
 *
 */
/* @ngInject */
export function ciStreamPolicyRule ($uibModal, UrlPolicy) {
  function getCondition (scope) {
    return (conditionRule) => UrlPolicy.getCondition(conditionRule, scope.entity)
  }
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciStreamPolicyRule.html',
    scope: {
      form: '=form',
      rules: '=rules',
      entity: '=entity',
      drmProfiles: '=drmProfiles',
      templateVariables: '=templateVariables',
      defaultStreamUrlSuggestions: '<?',
      playbackTokenGenerators: '=playbackTokenGenerators',
      subRule: '=subRule',
      showSelectFromTranscoder: '&?',
      transcoderOutputUrls: '<?',
      transcoderOutputUrlsLoading: '<?',
      refreshTranscoderOutputUrls: '&?',
      applyTranscoderUrlToRule: '&?'
    },
    link: function (scope) {
      scope.getCondition = getCondition(scope)

      scope.getRuleStreamUrlValue = function (rule) {
        const fieldName = rule && rule.id != null ? `streamUrl_${rule.id}` : null
        const field = fieldName && scope.form && scope.form[fieldName]
        const fromView = field && field.$viewValue
        if (fromView != null && String(fromView).trim() !== '') {
          return String(fromView).trim()
        }
        const url = rule && rule.then && rule.then.mediaUrl && rule.then.mediaUrl.mediaUrl
        return url != null && String(url).trim() !== '' ? String(url).trim() : ''
      }

      scope.hasRuleStreamUrl = function (rule) {
        return !!scope.getRuleStreamUrlValue(rule)
      }

      scope.onRuleStreamUrlChange = function (rule) {
        if (!rule.then) {
          rule.then = { mediaUrl: { drmProfiles: [] } }
        } else if (!rule.then.mediaUrl) {
          rule.then.mediaUrl = { drmProfiles: [] }
        }
        changeStreamUrl({ obj: rule.then })
      }

      scope.checkTrailerRequired = function () {
        return false
      }

      scope.canSelectFromTranscoder = function () {
        return typeof scope.showSelectFromTranscoder === 'function' && scope.showSelectFromTranscoder()
      }

      scope.urlPattern = URL_PATTERN_FOR_TEMPLATES
      scope.isRuleOpen = scope.entity._isNew() || true

      const setDefaultTrailerUrlValue = function (val, { obj, type }) {
        obj.mediaUrl[type] = val
      }
      scope.typeaheadDefaultTrailerUrl = new TypeaheadClass(scope.templateVariables, setDefaultTrailerUrlValue, changeStreamUrl)

      scope.highlightClassSimple = (model) => highlightClassSimple(scope.templateVariables, model)

      scope.changeStreamUrl = changeStreamUrl
      scope.multiSelectWithAll = multiSelectWithAll
      scope.getFilteredKeys = getFilteredKeys
      scope.filterDRM = filterDRM
      scope.initRule = function (rule) {
        if (!rule.then) {
          rule.then = { mediaUrl: { drmProfiles: [] } }
        } else {
          if (!rule.then.mediaUrl) {
            rule.then.mediaUrl = { drmProfiles: [] }
          } else if (!Array.isArray(rule.then.mediaUrl.drmProfiles)) {
            rule.then.mediaUrl.drmProfiles = []
          }
        }
        if (scope.entity.$itemType === 'show' && !rule.then.trailerUrl) {
          rule.then.trailerUrl = ''
        }
        if (rule.then.mediaUrl?.mediaUrl) {
          changeStreamUrl({ obj: rule.then })
        }
        if (scope.entity.$itemType === 'show' && rule.then.mediaUrl?.trailerUrl) {
          changeStreamUrl({ obj: rule.then, type: 'trailerUrl', isShow: true })
        }
      }

      scope.openRule = function (rule) {
        // eslint-disable-next-line no-undef
        event.stopPropagation()

        $uibModal.open({
          templateUrl: 'components/common/directives/contentItems/views/ciSPRuleDetails.html',
          controller: /* @ngInject */ function ($scope, $uibModalInstance) {
            $scope.streamPolicyOperators = streamPolicyOperators
            $scope.streamPolicyPropertiesNames = streamPolicyPropertiesNames
            $scope.getTermNames = function (terms, property) {
              return terms.map((term) => streamPolicyPropertiesNames[property].items?.[term]?.name || term).join(', ')
            }
            $scope.cancel = function () {
              $uibModalInstance.dismiss('cancel')
            }
            $scope.rule = rule
          },
          animation: false,
          size: 'lg',
          resolve: {
            rule
          }
        })
      }
    }
  }
}

/**
 * Directive for select DRM profile with connected fields
 *
 * @class ciDrmProfiles
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciDrmProfiles
 *
 */
/* @ngInject */
export function ciDrmProfiles () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciDrmProfiles.html',
    scope: {
      form: '=form',
      entity: '=entity',
      value: '=value',
      drmProfiles: '=drmProfiles',
      classItem: '=classItem',
      classContainer: '=classContainer',
      rule: '=rule',
      ruleId: '=ruleId'
    },
    link: function (scope) {
      scope.multiSelectWithAll = multiSelectWithAll
      scope.getFilteredKeys = getFilteredKeys
      scope.filterDRM = filterDRM
      scope.drm_account_types = DRM_ACCOUNT_TYPES

      scope.drms = scope.value.drmProfiles

      scope.deleteDRM = function (items) {
        if (!items.length || items[0].providerType === 'EZDRM') {
          for (const field of ['contentId']) {
            scope.value[field] = null
          }
        }

        if (!items.length || items[0].providerType !== 'EXPRESS_PLAY') {
          for (const field of ['kek']) {
            scope.value[field] = null
          }
        }

        if (!items.length || items[0].providerType !== scope.drm_account_types.IRDETO) {
          for (const field of ['entitlementPolicyId', 'bundleId']) {
            scope.value[field] = null
          }
        }
      }
    }
  }
}

/**
 * Directive for list of ACLs for item.
 *
 * @class ciAcl
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciAcl
 *
 */
export function ciAcl () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciAcl.html',
    scope: {
      entity: '=entity',
      acls: '=acls'

    },
    link: function (scope) {
    }
  }
}

/**
 * Directive for the Skip Intro / Skip Credits content controls (MW-12866 / MW-12965).
 *
 * Renders two sections (each: Enabled toggle + Segment End / Show Button From /
 * Show Button Until uib-timepickers) bound to the entity's `$skipIntro` /
 * `$skipCredits` Date mirrors, runs all field + cross-field validation, and
 * surfaces the helper text, the Default-URL / content-Length warning banners,
 * and (Episode only) the "Play Next" info hint.
 *
 * Submit is blocked by setting `skipIntroCredits` validity on the parent form
 * controller — both content forms already gate on `form.$invalid`.
 *
 * @class ciSkipIntroCredits
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciSkipIntroCredits
 */
export function ciSkipIntroCredits () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciSkipIntroCredits.html',
    scope: {
      form: '=form',
      entity: '=entity',
      contentLength: '=contentLength',
      itemType: '@?itemType'
    },
    link: function (scope, element) {
      scope.skip = { valid: true, intro: {}, credits: {}, overlap: false }
      scope.skipTouched = false
      scope.fieldTouch = {
        intro: {
          segmentEnd: { dirty: false, blurred: false },
          showButtonFrom: { dirty: false, blurred: false },
          showButtonUntil: { dirty: false, blurred: false }
        },
        credits: {
          segmentEnd: { dirty: false, blurred: false },
          showButtonFrom: { dirty: false, blurred: false },
          showButtonUntil: { dirty: false, blurred: false }
        }
      }
      scope.lastSeenSeconds = {
        intro: { segmentEnd: null, showButtonFrom: null, showButtonUntil: null },
        credits: { segmentEnd: null, showButtonFrom: null, showButtonUntil: null }
      }

      scope.markTouched = function () {
        scope.skipTouched = true
      }

      scope.showRequired = function (segment, field) {
        return !!(scope.form && scope.form.$submitted) ||
          (!!scope.fieldTouch[segment][field].dirty && !!scope.fieldTouch[segment][field].blurred)
      }

      scope.markFieldBlurred = function (segment, field) {
        scope.fieldTouch[segment][field].blurred = true
      }

      function secondsOf (date) {
        return date ? date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds() : -1
      }

      function fieldSeconds (segment, field) {
        const seg = segment === 'intro' ? scope.entity.$skipIntro : scope.entity.$skipCredits
        return secondsOf(seg && seg[field])
      }

      function parseSkipFieldAttr (wrapper) {
        const attr = wrapper.getAttribute('data-skip-field')
        if (!attr) return null
        const parts = attr.split('.')
        if (parts.length !== 2) return null
        return { segment: parts[0], field: parts[1] }
      }

      function onFocusOut (event) {
        const target = event.target
        if (!target || !target.closest) return

        const wrapper = target.closest('.timepicker[data-skip-field]')
        if (!wrapper || !element[0].contains(wrapper)) return

        const related = event.relatedTarget
        if (related && wrapper.contains(related)) return

        const parsed = parseSkipFieldAttr(wrapper)
        if (!parsed) return

        scope.$apply(function () {
          scope.markFieldBlurred(parsed.segment, parsed.field)
        })
      }

      if (element && element[0]) {
        element[0].addEventListener('focusout', onFocusOut)
      }

      function initLastSeenSeconds () {
        ;['intro', 'credits'].forEach(function (segment) {
          ;['segmentEnd', 'showButtonFrom', 'showButtonUntil'].forEach(function (field) {
            scope.lastSeenSeconds[segment][field] = fieldSeconds(segment, field)
          })
        })
      }

      scope.markFieldDirty = function (segment, field) {
        const current = fieldSeconds(segment, field)
        const lastSeen = scope.lastSeenSeconds[segment][field]
        if (lastSeen === null) {
          scope.lastSeenSeconds[segment][field] = current
          return
        }
        if (current !== lastSeen) {
          scope.fieldTouch[segment][field].dirty = true
          scope.lastSeenSeconds[segment][field] = current
        }
      }

      scope.onTimeFieldChange = function (segment, field) {
        const lastSeenBefore = scope.lastSeenSeconds[segment][field]
        const current = fieldSeconds(segment, field)
        const changed = lastSeenBefore !== null && current !== lastSeenBefore
        scope.markFieldDirty(segment, field)
        if (changed) {
          scope.markTouched()
        }
      }

      initLastSeenSeconds()

      function setFormValidity (valid) {
        if (scope.form && typeof scope.form.$setValidity === 'function') {
          scope.form.$setValidity('skipIntroCredits', valid)
        }
      }

      function recompute () {
        scope.skip = evaluateSkipSegments({
          intro: scope.entity.$skipIntro,
          credits: scope.entity.$skipCredits,
          contentLength: scope.contentLength
        })
        setFormValidity(scope.skip.valid)
      }

      function defaultStreamUrl () {
        const ds = scope.entity && scope.entity.defaultStream
        return ds && ds.mediaUrl ? ds.mediaUrl.mediaUrl : undefined
      }

      function skipSignature () {
        const i = scope.entity.$skipIntro || {}
        const c = scope.entity.$skipCredits || {}
        return [
          i.enabled, secondsOf(i.segmentEnd), secondsOf(i.showButtonFrom), secondsOf(i.showButtonUntil),
          c.enabled, secondsOf(c.segmentEnd), secondsOf(c.showButtonFrom), secondsOf(c.showButtonUntil),
          secondsOf(scope.contentLength)
        ].join('|')
      }

      // Non-blocking warning banners: only after the user edits these inputs.
      scope.defaultUrlChangedWarning = false
      scope.lengthChangedWarning = false
      let urlInitialized = false
      let lengthInitialized = false

      const stopUrlWatch = scope.$watch(defaultStreamUrl, function (nv, ov) {
        if (!urlInitialized) {
          urlInitialized = true
          return
        }
        if (nv !== ov) {
          scope.defaultUrlChangedWarning = true
        }
      })

      const stopLengthWatch = scope.$watch(function () {
        return secondsOf(scope.contentLength)
      }, function (nv, ov) {
        if (!lengthInitialized) {
          lengthInitialized = true
          return
        }
        if (nv !== ov) {
          scope.lengthChangedWarning = true
        }
      })

      const stopSkipWatch = scope.$watch(skipSignature, recompute)

      recompute()

      scope.$on('$destroy', function () {
        if (element && element[0]) {
          element[0].removeEventListener('focusout', onFocusOut)
        }
        stopUrlWatch()
        stopLengthWatch()
        stopSkipWatch()
        // Don't leave the form stuck invalid if the section is torn down.
        setFormValidity(true)
      })
    }
  }
}

/**
 * Directive for Offline Playback download settings on VOD and Episode forms.
 *
 * @class ciOfflinePlayback
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciOfflinePlayback
 */
export function ciOfflinePlayback () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciOfflinePlayback.html',
    scope: {
      form: '=form',
      entity: '=entity'
    },
    link: function (scope) {
      scope.hasSelectedDrmProfile = hasSelectedDrmProfile
    }
  }
}

export function openAssignContentSets ($state, $location, entity, isNew = false) {
  if (entity.$itemType === 'season') {
    $location.url(entity._publicUrl + '/contentSets')
    return
  }
  $state.go($state.router.globals.$current.parent.name + '.contentSets')
}

/**
 * Directive for list of assigned content sets for item.
 *
 * @class ciAssignedContentSets
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciAssignedContentSets
 *
 */
/* @ngInject */
/**
 * Searchable dropdown to pick a transcoder output URL and apply it to a URL field.
 *
 * @class ciSelectFromTranscoder
 * @memberof mwApp/common
 * @name mwApp/common:ciSelectFromTranscoder
 */
/* @ngInject */
export function ciSelectFromTranscoder ($timeout) {
  function normalizeActiveUrl (url) {
    if (url == null) return ''
    return String(url).trim()
  }

  function syncTranscoderResultHighlight (activeUrl, $select) {
    if (!$select) return

    const active = normalizeActiveUrl(activeUrl)
    const items = Array.isArray($select.items) ? $select.items : []
    let nextIndex = -1
    if (active) {
      const matchIndex = items.findIndex((url) => normalizeActiveUrl(url) === active)
      nextIndex = matchIndex >= 0 ? matchIndex : -1
    }
    $select.activeIndex = nextIndex
  }

  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciSelectFromTranscoder.html',
    scope: {
      urls: '<?',
      loading: '<?',
      refresh: '&',
      onUrlPick: '&',
      disabled: '<?',
      activeUrl: '<?'
    },
    link: function (scope, element) {
      scope.selectedUrl = undefined
      scope.pickedUrl = null
      scope._lastUiSelect = null

      function getHighlightUrl () {
        const active = normalizeActiveUrl(scope.activeUrl)
        if (active) return active
        return normalizeActiveUrl(scope.pickedUrl)
      }

      function resetDropdownSelection ($select) {
        scope.selectedUrl = undefined
        if ($select) {
          $select.selected = undefined
        }
      }

      scope.getFilteredUrls = function (search) {
        const list = Array.isArray(scope.urls) ? scope.urls : []
        if (!search) return list
        const query = String(search).toLowerCase()
        return list.filter((url) => String(url).toLowerCase().indexOf(query) !== -1)
      }

      scope.onRefresh = function () {
        if (typeof scope.refresh === 'function') {
          scope.refresh()
        }
      }

      function applyTranscoderPick (item, $select) {
        const url = item != null ? item : null
        if (!url) return
        scope.pickedUrl = url
        scope._lastUiSelect = $select
        resetDropdownSelection($select)
        if (typeof scope.onUrlPick === 'function') {
          scope.onUrlPick({ url: url })
        }
        syncTranscoderResultHighlight(getHighlightUrl(), $select)
      }

      $timeout(function () {
        const uiSelectEl = element.find('ui-select')
        const uiSelectScope = uiSelectEl.scope()
        const uiSelectCtrl = uiSelectEl.controller('uiSelect')
        if (!uiSelectScope || !uiSelectCtrl) return
        scope._lastUiSelect = uiSelectCtrl
        uiSelectScope.$on('uis:select', function (event, item) {
          if (item == null) return
          scope._lastUiSelect = uiSelectCtrl
          resetDropdownSelection(uiSelectCtrl)
        })
      })

      scope.onDropdownOpen = function ($select) {
        scope._lastUiSelect = $select
        $timeout(function () {
          syncTranscoderResultHighlight(getHighlightUrl(), $select)
        })
      }

      scope.$watch('activeUrl', function () {
        syncTranscoderResultHighlight(getHighlightUrl(), scope._lastUiSelect)
      })

      scope.$watch('selectedUrl', function (newVal) {
        if (newVal == null) return
        resetDropdownSelection(scope._lastUiSelect)
      })

      scope.handleTranscoderUrlPick = function (item, $select) {
        applyTranscoderPick(item, $select)
      }
    }
  }
}

export function ciAssignedContentSets () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciAssignedContentSets.html',
    scope: {
      form: '=form',
      entity: '=entity',
      allNetworks: '=?allNetworks'
    },
    link: function (scope) {
      scope.isCsOpen = false
      scope.deleteContentSet = function (index) {
        scope.form.$setDirty()
        scope.entity.contentSets.splice(index, 1)
      }
    }
  }
}
